import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { favoriteApi } from '../lib/api';
import { FireRating } from '../components/TalentCard';
import {
  Heart, Trash2, Loader2, MapPin, BadgeCheck, MessageSquare, 
  ExternalLink, Sparkles, ArrowLeft
} from 'lucide-react';

const Favorites = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?redirect=/favorites');
      return;
    }
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const loadFavorites = async () => {
    try {
      const response = await favoriteApi.getAll();
      setFavorites(response.data);
    } catch (error) {
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (talentId) => {
    setRemovingId(talentId);
    try {
      await favoriteApi.remove(talentId);
      setFavorites(prev => prev.filter(f => f.talent.user_id !== talentId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    } finally {
      setRemovingId(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="h-6 w-6 text-red-500 fill-red-500" />
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                My Favorites
              </h1>
            </div>
            <Badge variant="secondary" className="ml-2">
              {favorites.length} saved
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-bold mb-2">No favorites yet</h2>
            <p className="text-muted-foreground mb-6">
              Save bookees you like by clicking the heart icon on their profile
            </p>
            <Button onClick={() => navigate('/browse')} className="rounded-full">
              <Sparkles className="h-4 w-4 mr-2" />
              Browse Bookees
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {favorites.map(({ favorite_id, talent, added_at }) => (
              <Card 
                key={favorite_id} 
                className="hover:border-primary/50 transition-colors overflow-hidden"
                data-testid={`favorite-card-${talent.user_id}`}
              >
                <CardContent className="p-0">
                  {/* Cover/Header */}
                  <div className="h-24 bg-gradient-to-r from-primary/20 to-primary/5 relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 rounded-full bg-background/80 hover:bg-red-500/20 text-red-500"
                      onClick={() => handleRemove(talent.user_id)}
                      disabled={removingId === talent.user_id}
                      data-testid={`remove-favorite-${talent.user_id}`}
                    >
                      {removingId === talent.user_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Profile */}
                  <div className="px-4 pb-4 -mt-10">
                    <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
                      <AvatarImage src={talent.picture} />
                      <AvatarFallback className="text-2xl bg-primary/20">
                        {talent.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="mt-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 
                          className="font-bold text-lg cursor-pointer hover:text-primary"
                          onClick={() => navigate(`/talent/${talent.user_id}`)}
                        >
                          {talent.name}
                        </h3>
                        {talent.is_verified && (
                          <BadgeCheck className="h-5 w-5 text-blue-400" />
                        )}
                      </div>
                      
                      {talent.location && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {talent.location}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <FireRating rating={talent.rating || 0} />
                        {talent.main_category && (
                          <Badge variant="secondary" className="text-xs">
                            {talent.main_category}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Saved {new Date(added_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-lg"
                        onClick={() => navigate(`/messages?to=${talent.user_id}`)}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Message
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 rounded-lg"
                        onClick={() => navigate(`/talent/${talent.user_id}`)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
